import { eventNames, useCoreEvents } from '@carvana/core-events';
import '@carvana/showroom-css-theme/Theme.css';
import {
  DateOfBirthInput,
  dateOfBirthSchema,
  EmailInput,
  emailSchema,
  FirstNameInput,
  firstNameSchema,
  IncomeInput,
  incomeSchema,
  Input,
  LastNameInput,
  lastNameSchema,
  PhoneNumberInput,
  phoneNumberSchema,
  preferredLanguageSchema,
  preferredNameSchema
} from '@carvana/showroom-forms';
import { Option } from '@carvana/showroom-forms/Select';
import '@carvana/showroom-forms/Select/index.css';
import { Formik } from 'formik';
import isEqual from 'lodash.isequal';
import React, { useEffect } from 'react';
import { connect, useDispatch } from 'react-redux';
import * as Yup from 'yup';
import type { RootState } from '../../../store/types';
import { TabContent } from '../../../routes/RouterOutlet.styles';
import EditDisclaimer from '../../../shared/components/EditDisclaimer';
import ErrorPage from '../../../shared/components/ErrorPage';
import FormButtons from '../../../shared/components/FormButtons';
import { Column, Row } from '../../../shared/components/Layout';
import LoadingPage from '../../../shared/components/LoadingPage';
import PageTitle from '../../../shared/components/PageTitle';
import { getDisplayNameByPath } from '../../../utilities';
import { personalDetailsOperations, personalDetailsSelectors } from '../ducks';
import * as actions from '../ducks/actions';
import { LanguageSelect } from './EditPersonalInformation.styles';

const defaultContactsInformation = {
  name: {
    first: null,
    middle: null,
    last: null,
    suffix: null
  },
  phoneNumber: null,
  emailAddress: null
};

const editPersonalInformationFormValidationSchema = Yup.object().shape({
  shouldShowFirstName: Yup.boolean(),
  shouldShowLastName: Yup.boolean(),
  shouldShowPreferredName: Yup.boolean(),
  shouldShowEmailAddress: Yup.boolean(),
  shouldShowPhoneNumber: Yup.boolean(),
  shouldShowDateOfBirth: Yup.boolean(),
  shouldShowPreferredLanguage: Yup.boolean(),
  shouldShowIncome: Yup.boolean(),
  hasSsn: Yup.boolean(),
  firstName: Yup.string().when('shouldShowFirstName', {
    is: true,
    then: firstNameSchema,
    otherwise: Yup.string().notRequired()
  }),
  middleName: Yup.string().notRequired(),
  lastName: Yup.string().when('shouldShowLastName', {
    is: true,
    then: lastNameSchema,
    otherwise: Yup.string().notRequired()
  }),
  suffix: Yup.string().notRequired(),
  preferredName: Yup.string().when('shouldShowPreferredName', {
    is: true,
    then: preferredNameSchema,
    otherwise: Yup.string().notRequired().nullable()
  }),
  emailAddress: Yup.string().when('shouldShowEmailAddress', {
    is: true,
    then: emailSchema,
    otherwise: Yup.string().email().notRequired()
  }),
  phoneNumber: Yup.string().when('shouldShowPhoneNumber', {
    is: true,
    then: phoneNumberSchema,
    otherwise: Yup.string().notRequired()
  }),
  dateOfBirth: Yup.string().when('shouldShowDateOfBirth', {
    is: true,
    then: dateOfBirthSchema,
    otherwise: Yup.string().notRequired()
  }),
  preferredLanguage: Yup.string().when('shouldShowPreferredLanguage', {
    is: true,
    then: preferredLanguageSchema,
    otherwise: Yup.string().notRequired().nullable()
  }),
  income: Yup.string().when('shouldShowIncome', {
    is: true,
    then: incomeSchema,
    otherwise: Yup.string().notRequired()
  })
});

type OwnProps = {
  location?: {
    pathname: string;
    state: string;
  };
};

type StateProps = {
  autoFocus?: string;
  dateOfBirth?: string;
  emailAddress: string;
  firstName?: string;
  hasSsn?: boolean;
  income: string;
  isIn7DMBG: boolean;
  isLoadingPersonalDetails: boolean;
  isOrderInProgress: boolean;
  isOrderPlaced: boolean;
  isSubmitting: boolean;
  languages: string[];
  lastName?: string;
  middleName?: string;
  personalDetailsError: Record<string, unknown>;
  phoneNumber?: string;
  preferredLanguage: string | null;
  preferredName: string | null;
  sectionName: string;
  suffix?: string;
  userId: string;
};

type DispatchProps = {
  fetchUserProfileData: (userId: string) => void;
  refreshProfile: () => void; // TODO: Remove this as it is never provided
  updatePersonalInformation: (
    userPersonalInformation: Record<string, unknown>,
    sectionName: string,
    refreshProfile: () => void
  ) => void;
  updateUserProfileData: (userId: string, preferredName: string, preferredLanguage: string) => void;
};

function EditPersonalInformation({
  userId,
  firstName,
  middleName,
  lastName,
  suffix,
  emailAddress,
  phoneNumber,
  dateOfBirth,
  income,
  hasSsn,
  sectionName,
  isSubmitting,
  isLoadingPersonalDetails,
  personalDetailsError,
  refreshProfile,
  isOrderPlaced,
  autoFocus,
  preferredName,
  preferredLanguage,
  languages,
  fetchUserProfileData,
  updatePersonalInformation,
  updateUserProfileData,
  isOrderInProgress,
  isIn7DMBG
}: OwnProps & StateProps & DispatchProps) {
  const dispatch = useDispatch();

  const { emit } = useCoreEvents();
  const handleRefreshProfile = () => {
    // manually emit the header refresh profile core-events
    emit(eventNames.header.refreshProfile);
    // Legacy RefreshProfile
    return refreshProfile?.();
  };

  const isInRestrictedPeriod = isOrderInProgress || isIn7DMBG;
  const hasFirstName = Boolean(firstName);
  const hasLastName = Boolean(lastName);

  // Show field if not restricted OR if field is empty (needs to be populated)
  const shouldShowFirstName = !isInRestrictedPeriod || !hasFirstName;
  const shouldShowLastName = !isInRestrictedPeriod || !hasLastName;
  const shouldShowEmailAddress = Boolean(emailAddress);
  const shouldShowName = shouldShowFirstName || shouldShowLastName;
  const shouldShowPreferredName = Boolean(preferredName);
  const shouldShowPhoneNumber = Boolean(phoneNumber);
  const shouldShowDateOfBirth = Boolean(dateOfBirth);
  const shouldShowPreferredLanguage = Boolean(preferredLanguage);
  // eslint-disable-next-line radix
  const shouldShowIncome = Boolean(parseInt(income));
  const hasError = !isEqual(personalDetailsError, {});
  const shouldShowError = !isLoadingPersonalDetails && hasError;
  const shouldShowComponent = !isLoadingPersonalDetails && !hasError;

  const initialValues = {
    shouldShowFirstName,
    shouldShowLastName,
    shouldShowPreferredName,
    shouldShowEmailAddress,
    shouldShowPhoneNumber,
    shouldShowDateOfBirth,
    shouldShowPreferredLanguage,
    shouldShowIncome,
    hasSsn,
    firstName,
    middleName,
    lastName,
    suffix,
    emailAddress,
    phoneNumber,
    dateOfBirth,
    preferredName,
    preferredLanguage,
    income
  };

  const onSubmit = ({
    formInput
  }: {
    formInput: {
      dateOfBirth: string | undefined;
      emailAddress: string;
      firstName: string | undefined;
      hasSsn: boolean | undefined;
      income: string;
      lastName: string | undefined;
      middleName: string | undefined;
      phoneNumber: string | undefined;
      preferredName: string | undefined;
      preferredLanguage: string | undefined;
      shouldShowDateOfBirth: boolean;
      shouldShowEmailAddress: boolean;
      shouldShowFirstName: boolean;
      shouldShowIncome: boolean;
      shouldShowLastName: boolean;
      shouldShowPhoneNumber: boolean;
      shouldShowPreferredLanguage: boolean;
      shouldShowPreferredName: boolean;
      suffix: string | undefined;
    };
  }) => {
    const formattedIncome = formInput.income.replace(/[$,,]/g, '');
    const hasContactsChanged =
      formInput.firstName !== initialValues.firstName ||
      formInput.middleName !== initialValues.middleName ||
      formInput.lastName !== initialValues.lastName ||
      formInput.suffix !== initialValues.suffix ||
      formInput.phoneNumber !== initialValues.phoneNumber ||
      formInput.emailAddress !== initialValues.emailAddress;

    const hasPreferencesChanged =
      formInput.preferredName !== initialValues.preferredName ||
      formInput.preferredLanguage !== initialValues.preferredLanguage;

    const contactsInformation = {
      name: {
        first: formInput.firstName,
        middle: formInput.middleName,
        last: formInput.lastName,
        suffix: formInput.suffix
      },
      phoneNumber: formInput.phoneNumber || null,
      emailAddress: formInput.emailAddress
    };

    const formattedInput = {
      ...(hasContactsChanged ? contactsInformation : defaultContactsInformation),
      dateOfBirth:
        formInput.dateOfBirth !== initialValues.dateOfBirth ? formInput.dateOfBirth : null,
      income: formattedIncome !== initialValues.income ? formattedIncome : null,
      ssn: ''
    };

    if (hasPreferencesChanged) {
      const selectedLanguage =
        formInput.preferredLanguage !== initialValues.preferredLanguage
          ? formInput.preferredLanguage
          : initialValues.preferredLanguage;
      const selectedName =
        formInput.preferredName !== initialValues.preferredName
          ? formInput.preferredName
          : initialValues.preferredName;

      dispatch(actions.updatePersonalInformationBegin());
      updateUserProfileData(userId, selectedName ?? '', selectedLanguage ?? '');
      if (!hasContactsChanged) return;
    }
    updatePersonalInformation(formattedInput, sectionName, handleRefreshProfile);
  };

  /** Initialize user profile data */
  useEffect(() => {
    if (userId) {
      fetchUserProfileData(userId);
    }
  }, [userId]);

  return (
    <>
      <PageTitle title="Edit Personal Information" />
      {isOrderPlaced && <EditDisclaimer />}
      {isLoadingPersonalDetails && <LoadingPage />}
      {shouldShowError && <ErrorPage />}
      {shouldShowComponent && (
        <TabContent>
          <Formik
            enableReinitialize
            onSubmit={(formInput) => {
              onSubmit({
                formInput,
                initialValues,
                sectionName,
                updatePersonalInformation,
                handleRefreshProfile
              });
            }}
            initialValues={initialValues}
            validationSchema={editPersonalInformationFormValidationSchema}
            render={({
              touched,
              handleSubmit,
              handleChange,
              handleBlur,
              values,
              errors,
              setFieldValue
            }) => {
              const hasErrors = !isEqual(errors, {});
              const shouldDisableSubmit = isEqual(initialValues, values) || hasErrors;

              return (
                <form onSubmit={handleSubmit}>
                  {shouldShowName && (
                    <>
                      {shouldShowFirstName && (
                        <Row>
                          <Column>
                            <FirstNameInput
                              onChange={handleChange}
                              onBlur={handleBlur}
                              id="firstName"
                              data-testid="firstName"
                              type="text"
                              name="firstName"
                              variant="firstName"
                              touched={touched.firstName || false}
                              value={values.firstName || ''}
                              error={errors.firstName}
                            />
                          </Column>
                        </Row>
                      )}

                      {shouldShowFirstName && (
                        <Row>
                          <Column>
                            <Input
                              onChange={handleChange}
                              onBlur={handleBlur}
                              id="middleName"
                              type="text"
                              name="middleName"
                              label="Middle Name (Optional)"
                              touched={touched.middleName || false}
                              value={values.middleName || ''}
                              error={errors.middleName}
                            />
                          </Column>
                        </Row>
                      )}

                      {shouldShowLastName && (
                        <Row>
                          <Column>
                            <LastNameInput
                              onChange={handleChange}
                              onBlur={handleBlur}
                              id="lastName"
                              type="text"
                              name="lastName"
                              variant="lastName"
                              touched={touched.lastName}
                              value={values.lastName || ''}
                              error={errors.lastName}
                            />
                          </Column>
                        </Row>
                      )}

                      {shouldShowFirstName && (
                        <Row>
                          <Column>
                            <Input
                              onChange={handleChange}
                              onBlur={handleBlur}
                              id="suffix"
                              type="text"
                              name="suffix"
                              label="Suffix (Optional)"
                              touched={touched.suffix || false}
                              value={values.suffix || ''}
                              error={errors.suffix}
                            />
                          </Column>
                        </Row>
                      )}

                      {shouldShowFirstName && (
                        <Row>
                          <Column>
                            <Input
                              onChange={(event) => {
                                const { name, value } = event.target;
                                setFieldValue(name, value);
                              }}
                              onBlur={handleBlur}
                              id="preferredName"
                              type="text"
                              name="preferredName"
                              label="Preferred Name (Optional)"
                              touched={touched.preferredName || false}
                              value={values.preferredName || ''}
                              error={errors.preferredName}
                              autoFocus={autoFocus?.autoFocusInput === 'preferredName'}
                            />
                          </Column>
                        </Row>
                      )}
                    </>
                  )}
                  {shouldShowEmailAddress && (
                    <Row>
                      <Column>
                        <EmailInput
                          onChange={handleChange}
                          onBlur={handleBlur}
                          id="emailAddress"
                          type="email"
                          name="emailAddress"
                          variant="email"
                          touched={touched.emailAddress}
                          value={values.emailAddress || ''}
                          error={errors.emailAddress}
                        />
                      </Column>
                    </Row>
                  )}

                  {shouldShowPhoneNumber && (
                    <Row>
                      <Column>
                        <PhoneNumberInput
                          onChange={(event) => {
                            const { name, value } = event.target;
                            const formattedPhoneNumber = value.replace(/[-,(,), ]/g, '');
                            setFieldValue(name, formattedPhoneNumber);
                          }}
                          onBlur={handleBlur}
                          id="phoneNumber"
                          type="phone"
                          name="phoneNumber"
                          variant="phoneNumber"
                          touched={touched.phoneNumber}
                          value={values.phoneNumber || ''}
                          error={errors.phoneNumber}
                        />
                      </Column>
                    </Row>
                  )}

                  {shouldShowDateOfBirth && (
                    <Row>
                      <Column>
                        <DateOfBirthInput
                          onChange={handleChange}
                          onBlur={handleBlur}
                          id="dateOfBirth"
                          type="text"
                          name="dateOfBirth"
                          variant="dateOfBirth"
                          touched={touched.dateOfBirth}
                          value={values.dateOfBirth || ''}
                          error={errors.dateOfBirth}
                        />
                      </Column>
                    </Row>
                  )}
                  <Row>
                    <Column>
                      <LanguageSelect
                        id="languages-select"
                        value={values.preferredLanguage || ''}
                        onChange={(event) => setFieldValue('preferredLanguage', event.target.value)}
                        label="Language"
                      >
                        {languages.map((language) => {
                          return (
                            <Option key={language} value={language}>
                              {language}
                            </Option>
                          );
                        })}
                      </LanguageSelect>
                    </Column>
                  </Row>
                  {shouldShowIncome && (
                    <Row>
                      <Column>
                        <IncomeInput
                          onChange={(event) => {
                            const { name, value } = event.target;
                            const formattedIncome = value.replace(/[$,,]/g, '');
                            setFieldValue(name, formattedIncome);
                          }}
                          onBlur={handleBlur}
                          id="income"
                          type="text"
                          name="income"
                          variant="income"
                          touched={touched.income}
                          value={values.income || ''}
                          error={errors.income}
                        />
                      </Column>
                    </Row>
                  )}
                  <FormButtons isSubmitting={isSubmitting} isSubmitDisabled={shouldDisableSubmit} />
                </form>
              );
            }}
          />
        </TabContent>
      )}
    </>
  );
}

const mapDispatchToProps = {
  fetchUserProfileData: personalDetailsOperations.fetchUserProfileData,
  updatePersonalInformation: personalDetailsOperations.updatePersonalInformation,
  updateUserProfileData: personalDetailsOperations.updateUserProfileData
};

const mapStateToProps = (state: RootState, ownProps: OwnProps) => ({
  userId: personalDetailsSelectors.getUserId(state),
  firstName: personalDetailsSelectors.getFirstName(state),
  middleName: personalDetailsSelectors.getMiddleName(state),
  lastName: personalDetailsSelectors.getLastName(state),
  suffix: personalDetailsSelectors.getSuffix(state),
  emailAddress: personalDetailsSelectors.getEmailAddress(state),
  phoneNumber: personalDetailsSelectors.getPhoneNumber(state),
  dateOfBirth: personalDetailsSelectors.getDateOfBirth(state),
  income: personalDetailsSelectors.getIncome(state),
  sectionName: getDisplayNameByPath(ownProps?.location?.pathname ?? ''),
  isSubmitting: personalDetailsSelectors.getUpdatePersonalInformationIsLoading(state),
  isLoadingPersonalDetails: personalDetailsSelectors.getIsLoadingPersonalDetails(state),
  personalDetailsError: personalDetailsSelectors.getPersonalDetailsError(state),
  isOrderPlaced: personalDetailsSelectors.getIsOrderPlaced(state),
  autoFocus: ownProps?.location?.state,
  preferredName: personalDetailsSelectors.getPreferredName(state),
  preferredLanguage: personalDetailsSelectors.getPreferredLanguage(state),
  languages: personalDetailsSelectors.getLanguages(state),
  isOrderInProgress: personalDetailsSelectors.getIsOrderInProgress(state),
  isIn7DMBG: personalDetailsSelectors.getIsIn7DMBG(state)
});

export default connect(mapStateToProps, mapDispatchToProps)(EditPersonalInformation);
